import { BROWSER } from 'esm-env';
import { checkGreater, CustomError } from './core.ts';
import type { Json, TypeGuard } from './identity.ts';
import { isPresent } from './identity.ts';
import { filterIter, forEach, mapIter, rangeIter } from './iterators.ts';

const root = BROWSER ? window : globalThis;

// TODO: Firebase buckets
export type WebStorageMedium = 'localStorage' | 'sessionStorage';
export type StorageMedium = WebStorageMedium | 'memory';

export class StorageError extends CustomError {}

export interface Store {
  list(): IterableIterator<string>;
  get(id: string): string | undefined;
  set(id: string, value: string): void;
  delete(id: string): void;
  clear(): void;
}

const storageSeparator = BROWSER ? '/' : '_';

export function storePath(...components: string[]): string {
  return components.join(storageSeparator);
}

// TODO: In theory, I think we can construct StructuredStore with the appropriate PropGuard and make
// get/set type safe without being otherwise parameterized.
export class StructuredStore implements Store {
  private readonly prefix_: string;

  constructor(
    private readonly bucket_: Store,
    prefix?: string,
  ) {
    this.prefix_ = prefix !== undefined ? prefix + storageSeparator : '';
  }

  // TODO: Consider listing folders.
  folder(...components: string[]) {
    return new StructuredStore(this.bucket_, this.prefix_ + storePath(...components));
  }

  list() {
    const keys = filterIter(this.bucket_.list(), (id) => id.startsWith(this.prefix_));
    const pruned = mapIter(keys, (id) => id.slice(this.prefix_.length));
    return filterIter(pruned, (id) => !id.includes(storageSeparator));
  }

  get(id: string): string | undefined;
  get<T extends Json>(id: string, hasType: TypeGuard<T>): T | undefined;
  get<T extends Json>(id: string, hasType?: TypeGuard<T>): T | undefined {
    // TODO: Consider checking that id does not contain STORAGE_SEPARATOR, to avoid contamination of
    // subfolders.
    const value = this.bucket_.get(this.prefix_ + id);
    if (value !== undefined) {
      const json = JSON.parse(value) as unknown;
      if (hasType) {
        if (hasType(json)) {
          return json;
        }
      } else {
        if (typeof json === 'string') {
          return json as T;
        }
      }
    }
    return undefined;
  }

  set<T extends Json>(id: string, value: Readonly<T>) {
    // TODO: Consider checking that id does not contain STORAGE_SEPARATOR, to avoid contamination of
    // subfolders.
    this.bucket_.set(this.prefix_ + id, JSON.stringify(value));
  }

  delete(id: string) {
    // TODO: Consider checking that id does not contain STORAGE_SEPARATOR, to avoid contamination of
    // subfolders.
    this.bucket_.delete(this.prefix_ + id);
  }

  clear() {
    forEach(this.list(), (id) => {
      this.delete(id);
    });
  }
}

class WebStore implements Store {
  constructor(private readonly store_: Storage) {}

  list() {
    const range = rangeIter(this.store_.length);
    const keys = mapIter(range, (i) => this.store_.key(i));
    return filterIter(keys, isPresent);
  }

  get(id: string) {
    return this.store_.getItem(id) ?? undefined;
  }

  set(id: string, value: string) {
    this.store_.setItem(id, value);
  }

  delete(id: string) {
    this.store_.removeItem(id);
  }

  clear() {
    this.store_.clear();
  }
}

class MemoryStore implements Store {
  private readonly values_ = new Map<string, string>();

  list() {
    return this.values_.keys();
  }

  get(id: string) {
    return this.values_.get(id);
  }

  set(id: string, value: string) {
    this.values_.set(id, value);
  }

  delete(id: string) {
    this.values_.delete(id);
  }

  clear() {
    this.values_.clear();
  }
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
function isAvailable(type: WebStorageMedium): boolean {
  let storage: Storage | undefined;
  try {
    storage = root[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      e.name === 'QuotaExceededError' &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage !== undefined &&
      storage.length !== 0
    );
  }
}

const failoverChain: StorageMedium[] = ['localStorage', 'sessionStorage', 'memory'];
let memoryBucket: MemoryStore | undefined;

export function getBucket(
  medium: StorageMedium,
  folder: string[] = [],
  allowFallback = true,
): Store {
  const requested = medium;
  for (
    let index = checkGreater(failoverChain.indexOf(medium), -1);
    index < failoverChain.length;
    ++index
  ) {
    medium = failoverChain[index];
    let result: Store | undefined;
    if (medium === 'memory') {
      memoryBucket ??= new MemoryStore();
      result = memoryBucket;
    } else if (isAvailable(medium)) {
      result = new WebStore(root[medium]);
    } else if (!allowFallback) {
      throw new StorageError(`Storage medium '${medium}' is not available.`);
    } else {
      continue;
    }
    if (folder.length) {
      result = new StructuredStore(result, storePath(...folder));
    }
    if (requested !== medium) {
      console.warn(`'${requested}' unavailable, falling back to '${medium}'.`);
    }
    return result;
  }
  throw new StorageError(`No storage medium is available`);
}
